import React, { useEffect, useState } from 'react';
import Input, { Label } from '../Input';
import ModalFooter from '../ModalFooter';
import ModalHeader from '../ModalHeader';
import CenterModal from '../CenterModal';
import { useNpiInfo } from 'src/queries/useNpiInfo';
import { OrgUser } from 'src/stores/OrgUserStore';
import styles from './styles.module.scss';
import { ToastType } from 'src/stores/ToastStore';
import { ToastStoreObject } from 'src/stores/ToastStore';
import { isValidNpi } from 'common/lib/util/ProviderUtil';
import Flex from '../Flex';
import Button from '../Button';
import Icon from '../Icon';
import { ICONS } from 'src/utils/constants';
import LoadingIcon from '../LoadingIcon';
import { useSetUserNpi } from 'src/queries/useSetUserNpi';
import { useOrganizationUserById } from 'src/queries/useOrganizationUserById';

interface SetUserNpiModalProps {
    user: OrgUser;
    onClose: () => void;
}

function SetUserNpiModal(props: SetUserNpiModalProps) {
    const { user, onClose } = props;
    const [npi, setNpi] = useState('');
    const { data: orgUser, isFetching: isOrgUserFetching } = useOrganizationUserById(user.usrId);
    const {
        data: npiInfo,
        refetch: refetchNpiInfo,
        isSuccess: isNpiInfoSuccess,
        isFetching: isNpiInfoFetching,
        isError: isNpiInfoError,
        error: npiInfoError,
    } = useNpiInfo(npi);
    const {
        mutate: setUserNpi,
        isSuccess: isSetUserNpiSuccess,
        isError: isSetUserNpiError,
        error: setUserNpiError,
    } = useSetUserNpi(orgUser?.indexUser?.usrId);

    useEffect(() => {
        if (isNpiInfoError) {
            // @ts-ignore
            const errorMessage = npiInfoError?.response?.data?.message || 'Failed to get NPI information';
            ToastStoreObject.show(errorMessage, ToastType.Error);
        }
    }, [isNpiInfoSuccess, isNpiInfoError, npiInfoError]);

    useEffect(() => {
        if (isSetUserNpiSuccess) {
            onClose();
        }

        if (isSetUserNpiError) {
            // @ts-ignore
            const errorMessage = setUserNpiError?.response?.data?.message || 'Failed to set NPI';
            ToastStoreObject.show(errorMessage, ToastType.Error);
        }
    }, [isSetUserNpiSuccess, isSetUserNpiError, setUserNpiError]);

    return (
        <CenterModal isOpen={true} onClose={onClose}>
            <ModalHeader title={`Set ${user.frstNm} ${user.lastNm}'s (${user.usrNm}) NPI`} onClose={onClose} />
            {orgUser?.indexUser?.providerDetails?.npi ? (
                <div className={styles.contentWrap}>
                    <>
                        <Flex direction="column" gap={4} className={styles.npiInfoWrap}>
                            <div className={styles.npiInfoTitle}>User's Claimed NPI</div>
                            <Flex gap={16}>
                                <Flex value={1}>
                                    <Label text="NPI" />
                                </Flex>
                                <div className={styles.npiInfoValue}>{orgUser?.indexUser?.providerDetails?.npi}</div>
                            </Flex>
                            <Flex gap={16}>
                                <Flex value={1}>
                                    <Label text="First Name" />
                                </Flex>
                                <div className={styles.npiInfoValue}>
                                    {orgUser?.indexUser?.providerDetails?.firstName}
                                </div>
                            </Flex>
                            <Flex gap={16}>
                                <Flex value={1}>
                                    <Label text="Last Name" />
                                </Flex>
                                <div className={styles.npiInfoValue}>
                                    {orgUser?.indexUser?.providerDetails?.lastName}
                                </div>
                            </Flex>
                            <Flex gap={16}>
                                <Flex value={1}>
                                    <Label text="Credential" />
                                </Flex>
                                <div className={styles.npiInfoValue}>
                                    {orgUser?.indexUser?.providerDetails?.credential}
                                </div>
                            </Flex>
                        </Flex>
                    </>
                </div>
            ) : (
                <div className={styles.contentWrap}>
                    <Flex gap={16} align="center">
                        <Input type="number" label="NPI" value={npi} onChangeText={(text: string) => setNpi(text)} />
                        <Button
                            type="small"
                            className={styles.getNpiInfoButton}
                            leftIcon={<Icon name={ICONS.Search} size={16} />}
                            onClick={() => refetchNpiInfo()}
                            disabled={!isValidNpi(npi)}
                        />
                    </Flex>
                    {(isOrgUserFetching || isNpiInfoFetching) && !npiInfo && (
                        <Flex align="center" justify="center" className={styles.npiInfoWrap}>
                            <LoadingIcon />
                        </Flex>
                    )}
                    {npiInfo && (
                        <>
                            <Flex direction="column" gap={4} className={styles.npiInfoWrap}>
                                <div className={styles.npiInfoTitle}>NPI Information</div>
                                <Flex gap={16}>
                                    <Flex value={1}>
                                        <Label text="NPI" />
                                    </Flex>
                                    <div className={styles.npiInfoValue}>{npiInfo.npi}</div>
                                </Flex>
                                <Flex gap={16}>
                                    <Flex value={1}>
                                        <Label text="First Name" />
                                    </Flex>
                                    <div className={styles.npiInfoValue}>{npiInfo.firstName}</div>
                                </Flex>
                                <Flex gap={16}>
                                    <Flex value={1}>
                                        <Label text="Last Name" />
                                    </Flex>
                                    <div className={styles.npiInfoValue}>{npiInfo.lastName}</div>
                                </Flex>
                                <Flex gap={16}>
                                    <Flex value={1}>
                                        <Label text="Credential" />
                                    </Flex>
                                    <div className={styles.npiInfoValue}>{npiInfo.credential}</div>
                                </Flex>
                            </Flex>
                            <Flex align="center" className={styles.npiInfoDisclaimer}>
                                If this is the correct NPI information for this user, click the Set NPI button below.
                                Please note that NPI's can only be used once per user, so please be sure this is correct
                                before setting the NPI.
                            </Flex>
                        </>
                    )}
                </div>
            )}
            <ModalFooter
                primaryText={orgUser?.indexUser?.providerDetails?.npi ? 'Close' : 'Set NPI'}
                primaryClick={() => {
                    if (orgUser?.indexUser?.providerDetails?.npi) {
                        onClose();
                    } else if (npi && isValidNpi(npi)) {
                        setUserNpi({ npi });
                    }
                }}
                secondaryClick={() => onClose()}
                primaryProps={{ disabled: !npi || !isValidNpi(npi) }}
            />
        </CenterModal>
    );
}

export default SetUserNpiModal;
